/**
 * Session Tracker
 *
 * Manages user sessions with automatic heartbeats and storage checks.
 * Sessions persist across page reloads using the Dexie applicationSession
 * table. Each lock/unlock cycle ends the prior session and starts a fresh
 * one — see LockContext.lockScreen / unlockScreen.
 */

import { trackEvent, syncAnalyticsEvents, getStorageMetrics, getAppMetrics, isAppInstalled, trackStorageCheck } from '@/db/services/analytics_service';
import { isOnline } from './connectivity';
import db from '@/db/db';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('SessionTracker', LogLevel.DEBUG);

const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const STORAGE_CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes
// Worst-case wait for the session.end flush before we give up and let
// lockScreen proceed. Each analytics POST goes through fetchWithAuth with
// no explicit timeout, and navigator.onLine is famously unreliable on
// captive portals — without a cap here the user's "lock" click freezes
// the UI until the underlying fetch times out at the OS level.
const SESSION_END_FLUSH_TIMEOUT_MS = 2000;

/**
 * WebAuthn authenticator method used to unlock this session: "platform" for
 * built-in biometrics (Touch ID, Windows Hello), "cross-platform" for security
 * keys (e.g. YubiKey). Persisted with the session so session.end and heartbeat
 * events can report it too. Null when not available (e.g. App.tsx's cold-start
 * resume, where we haven't just authenticated).
 */
export type SessionAuthMethod = AuthenticatorAttachment | null;

export class SessionTracker {
  private sessionId: string | null = null;
  private sessionStartTime: number | null = null;
  private sessionAuthMethod: SessionAuthMethod = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private storageCheckInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Start a new session
   */
  async startSession(options: { authMethod?: SessionAuthMethod } = {}): Promise<void> {
    // Generate unique session ID
    this.sessionId = `session-${Date.now()}-${crypto.randomUUID()}`;
    this.sessionStartTime = Date.now();
    this.sessionAuthMethod = options.authMethod ?? null;

    // Store session ID in Dexie applicationSession
    const now = new Date().toISOString();
    await db.applicationSession.put({
      key: 'current_session_id',
      value: this.sessionId,
      updated_at: now
    });
    await db.applicationSession.put({
      key: 'session_start_time',
      value: this.sessionStartTime,
      updated_at: now
    });
    await db.applicationSession.put({
      key: 'session_auth_method',
      value: this.sessionAuthMethod,
      updated_at: now
    });

    // Get initial metrics
    const storageMetrics = await getStorageMetrics();
    const appMetrics = await getAppMetrics();

    // Track session start
    await trackEvent('session.start', {
      session_id: this.sessionId,
      auth_method: this.sessionAuthMethod ?? undefined,
      is_installed: isAppInstalled(),
      ...storageMetrics,
      ...appMetrics,
    });

    logger.log(`Started session: ${this.sessionId}`);

    // Start heartbeat
    this.startHeartbeat();

    // Start periodic storage checks
    this.startStorageChecks();
  }

  /**
   * Resume existing session or start a new one.
   *
   * `options.authMethod` is only forwarded when this call falls through to
   * startSession() — i.e. there was no existing session in Dexie. Callers
   * coming from a fresh unlock should always supply it; App.tsx's
   * cold-start resume omits it (no fresh auth).
   */
  async resumeSession(options: { authMethod?: SessionAuthMethod } = {}): Promise<void> {
    const sessionIdItem = await db.applicationSession.get('current_session_id');
    const sessionStartTimeItem = await db.applicationSession.get('session_start_time');
    const sessionAuthMethodItem = await db.applicationSession.get('session_auth_method');

    const existingSessionId = sessionIdItem?.value as string | undefined;
    const sessionStartTime = sessionStartTimeItem?.value as number | undefined;

    if (existingSessionId && sessionStartTime) {
      // Resume existing session
      this.sessionId = existingSessionId;
      this.sessionStartTime = sessionStartTime;
      this.sessionAuthMethod = (sessionAuthMethodItem?.value as SessionAuthMethod) ?? null;

      logger.log(`Resuming session: ${this.sessionId}`);

      // Start heartbeat
      this.startHeartbeat();

      // Start periodic storage checks
      this.startStorageChecks();
    } else {
      // Start new session
      await this.startSession(options);
    }
  }

  /**
   * End the current session
   */
  async endSession(): Promise<void> {
    if (!this.sessionId || !this.sessionStartTime) {
      logger.warn('No active session to end');
      return;
    }

    const sessionDuration = Date.now() - this.sessionStartTime;

    // Get final metrics
    const storageMetrics = await getStorageMetrics();
    const appMetrics = await getAppMetrics();

    // Track session end
    await trackEvent('session.end', {
      session_id: this.sessionId,
      session_duration_ms: sessionDuration,
      auth_method: this.sessionAuthMethod ?? undefined,
      ...storageMetrics,
      ...appMetrics,
    });

    // Flush pending analytics before the caller potentially closes the DB
    // (lockScreen calls endEncryptedAccess immediately after endSession).
    // trackEvent's auto-sync above is fire-and-forget; awaiting here makes
    // the push deterministic so session.end isn't stuck in IndexedDB until
    // the next unlock. Race against a deadline so a stalled fetch (captive
    // portal, server hung) can't freeze the lock UI — the events stay
    // queued locally and ship on the next online tick.
    if (isOnline()) {
      // Attach the failure handler BEFORE the race so a late rejection (the
      // timeout wins, syncAnalyticsEvents keeps running and then rejects)
      // is consumed instead of bubbling up as an unhandledrejection that
      // Sentry would report on every lock with a stalled network.
      const flush = syncAnalyticsEvents().catch(err => {
        logger.warn('Final analytics sync at session end failed', err);
      });
      await Promise.race([
        flush,
        new Promise<void>(resolve => setTimeout(resolve, SESSION_END_FLUSH_TIMEOUT_MS)),
      ]);
    }

    logger.log(`Ended session: ${this.sessionId} (duration: ${Math.round(sessionDuration / 1000)}s)`);

    // Clear session data
    this.stopHeartbeat();
    this.stopStorageChecks();
    await db.applicationSession.delete('current_session_id');
    await db.applicationSession.delete('session_start_time');
    await db.applicationSession.delete('session_auth_method');
    this.sessionId = null;
    this.sessionStartTime = null;
    this.sessionAuthMethod = null;
  }

  /**
   * Start periodic heartbeats
   */
  private startHeartbeat(): void {
    // Clear any existing heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(async () => {
      if (!this.sessionId) return;

      const sessionDuration = this.sessionStartTime
        ? Date.now() - this.sessionStartTime
        : 0;

      await trackEvent('session.heartbeat', {
        session_id: this.sessionId,
        session_duration_ms: sessionDuration,
        auth_method: this.sessionAuthMethod ?? undefined,
      });

      logger.log(`Heartbeat sent (duration: ${Math.round(sessionDuration / 1000)}s)`);
    }, HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Start periodic storage checks
   */
  private startStorageChecks(): void {
    // Clear any existing storage check
    if (this.storageCheckInterval) {
      clearInterval(this.storageCheckInterval);
    }

    this.storageCheckInterval = setInterval(async () => {
      await trackStorageCheck('periodic');
    }, STORAGE_CHECK_INTERVAL);
  }

  /**
   * Stop storage checks
   */
  private stopStorageChecks(): void {
    if (this.storageCheckInterval) {
      clearInterval(this.storageCheckInterval);
      this.storageCheckInterval = null;
    }
  }

}

// Singleton instance
let sessionTrackerInstance: SessionTracker | null = null;

/**
 * Get the singleton SessionTracker instance
 */
export function getSessionTracker(): SessionTracker {
  if (!sessionTrackerInstance) {
    sessionTrackerInstance = new SessionTracker();
  }
  return sessionTrackerInstance;
}
